import { useUser } from "@clerk/clerk-expo";
import type { proethicaOnboardingConfig } from "@constants/demo/praxis-proethica";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useExamContext } from "@features/exam";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import useClerkUser from "@hooks/useClerkUser";
import { useDevice } from "@hooks/useDevice";
import useUserInfo from "@hooks/useUserInfo";
import { DATE_FORMAT } from "@memorang/constants/src/date";
import { Box, Button, type IconType } from "@memorang/ui";
import { DateTimePicker } from "components";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import { Image } from "expo-image";
import { DateTime } from "luxon";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
	Linking,
	Animated as RNAnimated,
	useWindowDimensions,
} from "react-native";
import {
	Avatar,
	Card,
	Divider,
	IconButton,
	Surface,
	Text,
	TextInput,
} from "react-native-paper";
import Animated, { FadeIn } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import appleSliceIllustration from "../../../../assets/praxis-proethica/illustrations/apple-slice.png";
import bookStackIllustration from "../../../../assets/praxis-proethica/illustrations/book-stack.png";
import clipboardIllustration from "../../../../assets/praxis-proethica/illustrations/clipboard.png";
import badgeMagnifierIllustration from "../../../../assets/praxis-proethica/illustrations/graphic-badge-magnifier.png";
import envelopeIllustration from "../../../../assets/praxis-proethica/illustrations/green-envelope.png";
import handsIllustration from "../../../../assets/praxis-proethica/illustrations/hands.png";
import largePencilIllustration from "../../../../assets/praxis-proethica/illustrations/large-pencil.png";
import magnifyingGlassIllustration from "../../../../assets/praxis-proethica/illustrations/magnifying-glass.png";
import protractorIllustration from "../../../../assets/praxis-proethica/illustrations/protractor.png";
import ProethicaLogo from "../../../../assets/praxis-proethica/proethica-black-2024.svg";

type OnboardingConfig = typeof proethicaOnboardingConfig;
type OnboardingStep = OnboardingConfig["steps"][number];

type StepProps = {
	config: OnboardingConfig;
	isCompact: boolean;
};

type OnboardingFullscreenLayoutProps = {
	config: OnboardingConfig;
	currentStep: OnboardingStep;
	activeStepIndex: number;
	isCompact: boolean;
	children: ReactNode;
	canGoBack: boolean;
	isLastStep: boolean;
	onBack: () => void;
	onNext: () => void;
};

const contentMaxWidth = 1120;
const getStep = (config: OnboardingConfig, stepId: OnboardingStep["id"]) =>
	config.steps.find((step) => step.id === stepId) ?? config.steps[0];

const AnimatedProgressBar = ({
	progress,
	color,
	trackColor,
}: {
	progress: number;
	color: string;
	trackColor: string;
}) => {
	const animatedWidth = useRef(new RNAnimated.Value(progress)).current;

	useEffect(() => {
		RNAnimated.timing(animatedWidth, {
			toValue: progress,
			duration: 200,
			useNativeDriver: false,
		}).start();
	}, [animatedWidth, progress]);

	const widthInterpolated = animatedWidth.interpolate({
		inputRange: [0, 1],
		outputRange: ["0%", "100%"],
	});

	return (
		<Box height={4} width="100%" backgroundColor={trackColor}>
			<RNAnimated.View
				style={{ height: 4, backgroundColor: color, width: widthInterpolated }}
			/>
		</Box>
	);
};

const formatCourseTime = (durationMinutes: number) => {
	const hours = Math.floor(durationMinutes / 60);
	const minutes = durationMinutes % 60;
	if (!hours) {
		return `${minutes} min`;
	}
	return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
};

const PraxisOnboardingBackdrop = ({
	stepId,
	isCompact,
	bottomOffset,
}: {
	stepId: OnboardingStep["id"];
	isCompact: boolean;
	bottomOffset: number;
}) => {
	const theme = useAppTheme();

	if (isCompact) {
		return null;
	}

	const renderStepArt = () => {
		switch (stepId) {
			case "what-you-will-practice":
				return (
					<>
						<Image
							source={protractorIllustration}
							style={{
								position: "absolute",
								top: 56,
								right: -50,
								width: 420,
								height: 260,
								opacity: 0.26,
								transform: [{ rotate: "-3deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={clipboardIllustration}
							style={{
								position: "absolute",
								right: 34,
								bottom: 18,
								width: 360,
								height: 250,
								opacity: 0.24,
								transform: [{ rotate: "8deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={largePencilIllustration}
							style={{
								position: "absolute",
								left: 18,
								bottom: -80,
								width: 190,
								height: 344,
								opacity: 0.18,
								transform: [{ rotate: "-16deg" }],
							}}
							contentFit="contain"
						/>
					</>
				);
			case "confirm-details":
				return (
					<>
						<Image
							source={envelopeIllustration}
							style={{
								position: "absolute",
								top: 120,
								right: -44,
								width: 430,
								height: 322,
								opacity: 0.25,
								transform: [{ rotate: "-6deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={magnifyingGlassIllustration}
							style={{
								position: "absolute",
								left: 82,
								bottom: 34,
								width: 280,
								height: 300,
								opacity: 0.16,
								transform: [{ rotate: "18deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={appleSliceIllustration}
							style={{
								position: "absolute",
								right: 340,
								bottom: 88,
								width: 210,
								height: 170,
								opacity: 0.18,
							}}
							contentFit="contain"
						/>
					</>
				);
			case "how-you-will-begin":
				return (
					<>
						<Image
							source={handsIllustration}
							style={{
								position: "absolute",
								right: -32,
								bottom: -82,
								width: 260,
								height: 474,
								opacity: 0.22,
								transform: [{ rotate: "4deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={bookStackIllustration}
							style={{
								position: "absolute",
								top: 42,
								right: 180,
								width: 330,
								height: 318,
								opacity: 0.22,
								transform: [{ rotate: "5deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={badgeMagnifierIllustration}
							style={{
								position: "absolute",
								left: 104,
								bottom: 92,
								width: 162,
								height: 162,
								opacity: 0.16,
							}}
							contentFit="contain"
						/>
					</>
				);
			default:
				return (
					<>
						<Image
							source={bookStackIllustration}
							style={{
								position: "absolute",
								top: -22,
								right: -40,
								width: 390,
								height: 375,
								opacity: 0.34,
								transform: [{ rotate: "-4deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={clipboardIllustration}
							style={{
								position: "absolute",
								top: 230,
								right: -106,
								width: 390,
								height: 270,
								opacity: 0.28,
								transform: [{ rotate: "5deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={magnifyingGlassIllustration}
							style={{
								position: "absolute",
								top: 112,
								right: 430,
								width: 320,
								height: 342,
								opacity: 0.22,
								transform: [{ rotate: "-20deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={largePencilIllustration}
							style={{
								position: "absolute",
								right: 466,
								bottom: -98,
								width: 210,
								height: 380,
								opacity: 0.24,
								transform: [{ rotate: "-13deg" }],
							}}
							contentFit="contain"
						/>
						<Image
							source={badgeMagnifierIllustration}
							style={{
								position: "absolute",
								right: 220,
								bottom: 76,
								width: 158,
								height: 158,
								opacity: 0.32,
							}}
							contentFit="contain"
						/>
					</>
				);
		}
	};

	return (
		<Box
			style={{
				position: "absolute",
				top: 79,
				right: 0,
				bottom: bottomOffset,
				left: 0,
				zIndex: 0,
				overflow: "hidden",
				pointerEvents: "none",
			}}
		>
			<Box
				style={{
					position: "absolute",
					top: 78,
					right: 84,
					width: 560,
					height: 560,
					borderRadius: 235,
					backgroundColor: theme.colors.secondaryContainer,
					opacity: 0.34,
				}}
			/>
			<Box
				style={{
					position: "absolute",
					top: 108,
					right: 0,
					width: 710,
					height: 470,
					backgroundColor: theme.colors.background,
					opacity: 0.58,
					transform: [{ rotate: "-8deg" }],
				}}
			/>
			{renderStepArt()}
			<Box
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					bottom: 0,
					width: "58%",
					backgroundColor: theme.colors.background,
					opacity: 0.72,
				}}
			/>
		</Box>
	);
};

export const OnboardingProgress = ({
	steps,
	activeStepIndex,
	isCompact,
}: {
	steps: OnboardingConfig["steps"];
	activeStepIndex: number;
	isCompact: boolean;
}) => {
	const theme = useAppTheme();

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent={isCompact ? "flex-start" : "center"}
			flexWrap="wrap"
			style={{ gap: 8 }}
		>
			{steps.map((step, index) => {
				const isActive = index === activeStepIndex;
				const isComplete = index < activeStepIndex;

				return (
					<Box
						key={step.id}
						flexDirection="row"
						alignItems="center"
						backgroundColor={
							isActive
								? theme.colors.primary
								: isComplete
									? theme.colors.secondaryContainer
									: theme.colors.surface
						}
						borderColor={
							isActive ? theme.colors.primary : theme.colors.outlineVariant
						}
						borderWidth={1}
						borderRadius={theme.roundness}
						paddingVertical={6}
						paddingHorizontal={10}
						style={{ gap: 6 }}
					>
						<Text
							variant="labelSmall"
							style={{
								color: isActive
									? theme.colors.onPrimary
									: theme.colors.onSurfaceVariant,
								fontWeight: "700",
							}}
						>
							{index + 1}
						</Text>
						{isCompact ? null : (
							<Text
								variant="labelSmall"
								style={{
									color: isActive
										? theme.colors.onPrimary
										: theme.colors.onSurfaceVariant,
									fontWeight: "700",
								}}
							>
								{step.title}
							</Text>
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export const OnboardingFullscreenLayout = ({
	config,
	currentStep,
	activeStepIndex,
	isCompact,
	children,
	canGoBack,
	isLastStep,
	onBack,
	onNext,
}: OnboardingFullscreenLayoutProps) => {
	const theme = useAppTheme();
	const supportEmail = useAppStore((store) => store.supportEmail);
	const insets = useSafeAreaInsets();
	const bottomInset = Math.max(insets.bottom - 16, 0);
	const bottomBarHeight = 80 + bottomInset + (isCompact ? 0 : 16);
	const headerSideWidth = isCompact ? 132 : 220;
	const logoWidth = isCompact ? 132 : 180;
	const progressValue = (activeStepIndex + 1) / config.steps.length;

	const handleHelp = () => {
		Linking.openURL(`mailto:${supportEmail}`);
	};

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				paddingHorizontal={isCompact ? 20 : 40}
				paddingVertical={18}
				borderBottomWidth={1}
				borderBottomColor={theme.colors.outlineVariant}
				backgroundColor={theme.colors.background}
			>
				<Box width={headerSideWidth} height={34} justifyContent="center">
					<ProethicaLogo
						width={logoWidth}
						height={34}
						accessibilityLabel="Praxis ProEthica"
					/>
				</Box>
				<Box flex={1} alignItems="center">
					{isCompact ? null : (
						<OnboardingProgress
							steps={config.steps}
							activeStepIndex={activeStepIndex}
							isCompact={isCompact}
						/>
					)}
				</Box>
				<Box width={headerSideWidth} alignItems="flex-end">
					<Button mode="text" icon="help-circle-outline" onPress={handleHelp}>
						Help
					</Button>
				</Box>
			</Box>
			<AnimatedProgressBar
				progress={progressValue}
				color={theme.colors.primary}
				trackColor={theme.colors.surfaceVariant}
			/>
			<PraxisOnboardingBackdrop
				stepId={currentStep.id}
				isCompact={isCompact}
				bottomOffset={bottomBarHeight}
			/>
			{isCompact ? (
				<Box paddingHorizontal={20} paddingTop={16}>
					<OnboardingProgress
						steps={config.steps}
						activeStepIndex={activeStepIndex}
						isCompact={isCompact}
					/>
				</Box>
			) : null}
			<Animated.ScrollView
				key={activeStepIndex}
				entering={FadeIn.duration(300)}
				style={{ flex: 1, zIndex: 1 }}
				contentContainerStyle={{ flexGrow: 1, paddingBottom: bottomBarHeight }}
			>
				<Box
					flex={1}
					width="100%"
					maxWidth={contentMaxWidth}
					alignSelf="center"
					justifyContent="center"
					paddingHorizontal={isCompact ? 20 : 40}
					paddingVertical={isCompact ? 28 : 56}
					style={{ gap: isCompact ? 24 : 32 }}
				>
					{children}
				</Box>
			</Animated.ScrollView>
			<Surface
				elevation={4}
				style={{
					position: "absolute",
					right: 0,
					bottom: 0,
					left: 0,
					zIndex: 100000,
					height: bottomBarHeight,
					justifyContent: isCompact ? "flex-start" : "center",
					paddingTop: isCompact ? 12 : 0,
					paddingHorizontal: isCompact ? 20 : 40,
					backgroundColor: theme.colors.elevation.level1,
					borderTopWidth: 1,
					borderTopColor: theme.colors.outlineVariant,
				}}
			>
				<Box
					width="100%"
					maxWidth={contentMaxWidth}
					alignSelf="center"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
					style={{ gap: 12 }}
				>
					{canGoBack ? (
						<Button mode="text" icon="arrow-left" onPress={onBack}>
							Back
						</Button>
					) : (
						<Box width={isCompact ? 80 : 120} />
					)}
					<Button
						mode="contained"
						icon={isLastStep ? "play" : "arrow-right"}
						onPress={onNext}
						contentStyle={{ flexDirection: "row-reverse" }}
						style={{
							borderRadius: theme.roundness,
							minWidth: isCompact ? 150 : 190,
						}}
					>
						{currentStep.cta}
					</Button>
				</Box>
			</Surface>
		</Box>
	);
};

const DetailsRow = ({
	icon,
	label,
	value,
	editable,
	isEditing,
	onChangeText,
	onEditPress,
	onDonePress,
}: {
	icon: IconType;
	label: string;
	value: string;
	editable?: boolean;
	isEditing?: boolean;
	onChangeText?: (value: string) => void;
	onEditPress?: () => void;
	onDonePress?: () => void;
}) => {
	const theme = useAppTheme();

	return (
		<Box flexDirection="row" alignItems="center" paddingVertical={14} gap={12}>
			<Avatar.Icon
				size={36}
				icon={icon}
				style={{ backgroundColor: theme.colors.surfaceVariant }}
				color={theme.colors.primary}
			/>
			<Box flex={1} minWidth={0}>
				<Text
					variant="labelMedium"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{label}
				</Text>
				{isEditing ? (
					<TextInput
						autoFocus
						value={value}
						onChangeText={onChangeText}
						mode="flat"
						dense={true}
						underlineColor="transparent"
						activeUnderlineColor={theme.colors.primary}
						style={{
							backgroundColor: "transparent",
							fontSize: 16,
							paddingHorizontal: 0,
						}}
						contentStyle={{ paddingHorizontal: 0 }}
					/>
				) : (
					<Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
						{value}
					</Text>
				)}
			</Box>
			{editable ? (
				<IconButton
					icon={isEditing ? "check" : "pencil-outline"}
					mode="contained-tonal"
					size={20}
					onPress={isEditing ? onDonePress : onEditPress}
					accessibilityLabel={isEditing ? `Save ${label}` : `Edit ${label}`}
					style={{ marginRight: -4 }}
				/>
			) : null}
		</Box>
	);
};

const ModuleSyllabusRow = ({
	module,
	index,
}: {
	module: OnboardingConfig["modules"][number];
	index: number;
}) => {
	const theme = useAppTheme();
	const thumbnailSource =
		module.thumbnailSource ??
		(module.thumbnailUrl ? { uri: module.thumbnailUrl } : undefined);

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingVertical={14}
			paddingHorizontal={16}
			style={{
				borderTopWidth: index === 0 ? 0 : 1,
				borderTopColor: theme.colors.outlineVariant,
				gap: 14,
			}}
		>
			<Box width={58} height={58}>
				{thumbnailSource ? (
					<Image
						source={thumbnailSource}
						style={{
							width: 58,
							height: 58,
							borderRadius: theme.roundness,
						}}
						contentFit="contain"
						alt={module.title}
					/>
				) : (
					<Box
						width={58}
						height={58}
						alignItems="center"
						justifyContent="center"
						backgroundColor={theme.colors.surfaceVariant}
						borderRadius={theme.roundness}
					>
						<Avatar.Icon
							size={32}
							icon="book-open-outline"
							style={{ backgroundColor: "transparent" }}
							color={theme.colors.primary}
						/>
					</Box>
				)}
			</Box>
			<Box flex={1} minWidth={0} gap={4}>
				<Text
					variant="labelMedium"
					style={{ color: theme.colors.primary, fontWeight: "800" }}
				>
					Module {module.moduleNumber}
				</Text>
				<Text
					variant="titleSmall"
					numberOfLines={2}
					ellipsizeMode="tail"
					style={{ color: theme.colors.onSurface, fontWeight: "800" }}
				>
					{module.title}
				</Text>
				<Text
					variant="bodySmall"
					numberOfLines={2}
					ellipsizeMode="tail"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{module.subtitle}
				</Text>
			</Box>
			<Box alignItems="flex-end" minWidth={116}>
				<ModuleMetadataChips
					typeLabel={module.moduleKindLabel}
					durationLabel={module.durationLabel}
				/>
			</Box>
		</Box>
	);
};

const ExpectationItem = ({
	icon,
	title,
	description,
}: {
	icon: IconType;
	title: string;
	description: string;
}) => {
	const theme = useAppTheme();

	return (
		<Box flexDirection="row" alignItems="flex-start" gap={12}>
			<Avatar.Icon
				size={34}
				icon={icon}
				style={{ backgroundColor: theme.colors.surfaceVariant }}
				color={theme.colors.primary}
			/>
			<Box flex={1} minWidth={0}>
				<Text
					variant="titleSmall"
					style={{ color: theme.colors.onSurface, fontWeight: "700" }}
				>
					{title}
				</Text>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{description}
				</Text>
			</Box>
		</Box>
	);
};

const CourseStat = ({
	value,
	label,
}: {
	value: string;
	label: string;
}) => {
	const theme = useAppTheme();

	return (
		<Box
			flex={1}
			minWidth={120}
			backgroundColor={theme.colors.surfaceVariant}
			borderRadius={theme.roundness}
			padding={12}
			gap={2}
		>
			<Text
				variant="titleMedium"
				style={{ color: theme.colors.primary, fontWeight: "800" }}
			>
				{value}
			</Text>
			<Text
				variant="labelMedium"
				style={{ color: theme.colors.onSurfaceVariant, fontWeight: "700" }}
			>
				{label}
			</Text>
		</Box>
	);
};

export const WelcomeStep = ({ config, isCompact }: StepProps) => {
	const theme = useAppTheme();
	const step = getStep(config, "why-it-matters");

	return (
		<Box
			flexDirection={isCompact ? "column" : "row"}
			alignItems="stretch"
			style={{
				gap: isCompact ? 18 : 32,
				position: "relative",
				overflow: "visible",
			}}
		>
			<Box flex={1} justifyContent="center" style={{ gap: 18, zIndex: 1 }}>
				<Text
					variant={isCompact ? "headlineMedium" : "displaySmall"}
					style={{ color: theme.colors.onBackground, fontWeight: "800" }}
				>
					{step.headline}
				</Text>
				<Text
					variant="titleMedium"
					style={{ color: theme.colors.onSurfaceVariant, lineHeight: 28 }}
				>
					{step.description}
				</Text>
			</Box>
			<Card
				mode="outlined"
				style={{
					flex: 1,
					borderRadius: theme.roundness,
					backgroundColor: theme.colors.surface,
					borderColor: theme.colors.outlineVariant,
					zIndex: 1,
				}}
			>
				<Box padding={20} gap={12}>
					<Text variant="titleMedium" style={{ fontWeight: "800" }}>
						What you will strengthen
					</Text>
					{config.outcomes.map((outcome) => (
						<ExpectationItem
							key={outcome.title}
							icon={outcome.icon as IconType}
							title={outcome.title}
							description={outcome.description}
						/>
					))}
				</Box>
			</Card>
		</Box>
	);
};

type EditableDetailField = "NAME" | "INSTITUTION" | "DISTRICT";

type ConfirmDetailsFormValues = {
	name: string;
	institution: string;
	district: string;
};

const confirmDetailsFieldKey: Record<
	EditableDetailField,
	keyof ConfirmDetailsFormValues
> = {
	NAME: "name",
	INSTITUTION: "institution",
	DISTRICT: "district",
};

export const ConfirmDetailsStep = ({ config }: StepProps) => {
	const theme = useAppTheme();
	const step = getStep(config, "confirm-details");
	const [editingField, setEditingField] = useState<EditableDetailField | null>(
		null,
	);
	const [isSavingField, setIsSavingField] = useState(false);
	const [formValues, setFormValues] = useState<ConfirmDetailsFormValues>({
		name: "",
		institution: "",
		district: "",
	});
	const [showGoalDatePicker, setShowGoalDatePicker] = useState(false);

	const { updateExamDate, currentExamContext, isExamContextLoading } =
		useExamContext();
	const defaultGoalDate = useMemo(() => DateTime.now().plus({ months: 3 }), []);
	const defaultGoalDateFormatted = useMemo(
		() => defaultGoalDate.toFormat(DATE_FORMAT),
		[defaultGoalDate],
	);
	const goalDate =
		currentExamContext.examDate ||
		(isExamContextLoading ? undefined : defaultGoalDateFormatted);
	const { email } = useClerkUser();
	const { user } = useUser();
	const userInfo = useUserInfo();

	useEffect(() => {
		if (editingField) {
			return;
		}

		setFormValues((prev) => ({
			name: userInfo?.name ?? user?.fullName ?? prev.name,
			institution:
				(user?.unsafeMetadata?.institution as string | undefined) ??
				prev.institution,
			district:
				(user?.unsafeMetadata?.district as string | undefined) ?? prev.district,
		}));
	}, [
		editingField,
		user?.fullName,
		user?.unsafeMetadata?.district,
		user?.unsafeMetadata?.institution,
		userInfo?.name,
	]);

	useEffect(() => {
		if (isExamContextLoading || currentExamContext.examDate) {
			return;
		}

		updateExamDate(defaultGoalDate.toJSDate());
	}, [
		currentExamContext.examDate,
		defaultGoalDate,
		isExamContextLoading,
		updateExamDate,
	]);

	const getFieldDisplayValue = (field: EditableDetailField, value: string) =>
		editingField === field ? value : value || "Not set";

	const handleFieldEditPress = (field: EditableDetailField) => {
		setEditingField(field);
	};

	const handleFieldChange = (field: EditableDetailField, value: string) => {
		const fieldKey = confirmDetailsFieldKey[field];
		setFormValues((prev) => ({ ...prev, [fieldKey]: value }));
	};

	const handleFieldSave = async (field: EditableDetailField) => {
		if (!user) {
			setEditingField(null);
			return;
		}

		const fieldKey = confirmDetailsFieldKey[field];
		const trimmedValue = formValues[fieldKey].trim();
		if (!trimmedValue) {
			setEditingField(null);
			return;
		}

		setIsSavingField(true);

		try {
			if (field === "NAME") {
				const [firstName, ...lastNameParts] = trimmedValue.split(/\s+/);
				await user.update({
					firstName,
					lastName: lastNameParts.join(" "),
				});
			} else {
				const metadataKey =
					field === "INSTITUTION" ? "institution" : "district";
				await user.update({
					unsafeMetadata: {
						...(user.unsafeMetadata ?? {}),
						[metadataKey]: trimmedValue,
					},
				});
			}

			setFormValues((prev) => ({ ...prev, [fieldKey]: trimmedValue }));
			setEditingField(null);
		} catch (error) {
			captureErrorToSentry(
				new Error(`Failed to update Clerk user ${fieldKey}`),
				{
					error: JSON.stringify(error),
				},
			);
		} finally {
			setIsSavingField(false);
		}
	};

	const isFieldEditable = (field: EditableDetailField) =>
		!isSavingField && (editingField === null || editingField === field);

	const handleGoalDatePicked = (date: Date) => {
		updateExamDate(date);
		setShowGoalDatePicker(false);
	};

	return (
		<>
			<Box alignSelf="center" width="100%" maxWidth={720} gap={18}>
				<Box gap={8}>
					<Text
						variant="headlineMedium"
						style={{ color: theme.colors.onBackground, fontWeight: "800" }}
					>
						{step.title}
					</Text>
					<Text
						variant="bodyLarge"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{step.description}
					</Text>
				</Box>
				<Card
					mode="outlined"
					style={{
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surface,
						borderColor: theme.colors.outlineVariant,
					}}
				>
					<Box paddingHorizontal={20} paddingVertical={8}>
						<DetailsRow
							icon="account-outline"
							label="Name"
							value={getFieldDisplayValue("NAME", formValues.name)}
							editable={isFieldEditable("NAME")}
							isEditing={editingField === "NAME"}
							onChangeText={(value) => handleFieldChange("NAME", value)}
							onEditPress={() => handleFieldEditPress("NAME")}
							onDonePress={() => handleFieldSave("NAME")}
						/>
						<Divider />
						<DetailsRow
							icon="email-outline"
							label="Email"
							value={email || "Not set"}
						/>
						<Divider />
						<DetailsRow
							icon="office-building-outline"
							label="Institution"
							value={getFieldDisplayValue(
								"INSTITUTION",
								formValues.institution,
							)}
							editable={isFieldEditable("INSTITUTION")}
							isEditing={editingField === "INSTITUTION"}
							onChangeText={(value) => handleFieldChange("INSTITUTION", value)}
							onEditPress={() => handleFieldEditPress("INSTITUTION")}
							onDonePress={() => handleFieldSave("INSTITUTION")}
						/>
						<Divider />
						<DetailsRow
							icon="domain"
							label="District"
							value={getFieldDisplayValue("DISTRICT", formValues.district)}
							editable={isFieldEditable("DISTRICT")}
							isEditing={editingField === "DISTRICT"}
							onChangeText={(value) => handleFieldChange("DISTRICT", value)}
							onEditPress={() => handleFieldEditPress("DISTRICT")}
							onDonePress={() => handleFieldSave("DISTRICT")}
						/>
						<Divider />
						<DetailsRow
							icon="school-outline"
							label="Curriculum"
							value="Praxis ProEthica"
						/>
						<Divider />
						<DetailsRow
							icon="calendar-outline"
							label="Goal Date"
							value={goalDate ?? "Not set"}
							editable={true}
							onEditPress={() => setShowGoalDatePicker(true)}
						/>
					</Box>
				</Card>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
					gap={4}
				>
					<Icon
						name="check-circle-outline"
						size={16}
						color={theme.colors.onBackground}
					/>
					<Text
						variant="bodySmall"
						style={{ fontStyle: "italic", color: theme.colors.onBackground }}
					>
						You can change this later
					</Text>
				</Box>
			</Box>
			{showGoalDatePicker ? (
				<DateTimePicker
					isVisible
					initialDate={goalDate ?? defaultGoalDateFormatted}
					okButtonLabel="Set Goal Date"
					toolbarTitle="Select Goal Date"
					onCancel={() => setShowGoalDatePicker(false)}
					onConfirm={handleGoalDatePicked}
				/>
			) : null}
		</>
	);
};

export const CurriculumStep = ({ config, isCompact }: StepProps) => {
	const theme = useAppTheme();
	const step = getStep(config, "what-you-will-practice");
	const totalDuration = config.modules.reduce(
		(total, module) => total + module.durationMinutes,
		0,
	);
	const totalActivities = config.modules.reduce(
		(total, module) => total + module.activityCount,
		0,
	);
	return (
		<Box gap={20}>
			<Card
				mode="outlined"
				style={{
					borderRadius: theme.roundness,
					backgroundColor: theme.colors.surface,
					borderColor: theme.colors.outlineVariant,
				}}
			>
				<Box padding={isCompact ? 18 : 24} gap={20}>
					<Box
						flexDirection={isCompact ? "column" : "row"}
						alignItems={isCompact ? "stretch" : "flex-start"}
						gap={24}
					>
						<Box flex={1.45} minWidth={0} gap={10}>
							<Text
								variant={isCompact ? "headlineSmall" : "headlineMedium"}
								style={{ color: theme.colors.onSurface, fontWeight: "800" }}
							>
								ProEthica Course Overview
							</Text>
							<Text
								variant="titleMedium"
								style={{
									color: theme.colors.onSurfaceVariant,
									lineHeight: 27,
								}}
							>
								{step.description}
							</Text>
						</Box>
						<Box
							flex={1}
							minWidth={isCompact ? undefined : 320}
							borderColor={theme.colors.outlineVariant}
							borderWidth={1}
							borderRadius={theme.roundness}
							padding={14}
							gap={12}
						>
							<Text variant="titleSmall" style={{ fontWeight: "800" }}>
								This course includes
							</Text>
							<Box flexDirection="row" flexWrap="wrap" gap={8}>
								<CourseStat
									value={`${config.modules.length}`}
									label="Modules"
								/>
								<CourseStat
									value={formatCourseTime(totalDuration)}
									label="Estimated time"
								/>
								<CourseStat value={`${totalActivities}`} label="Activities" />
							</Box>
						</Box>
					</Box>
				</Box>
			</Card>

			<Card
				mode="outlined"
				style={{
					borderRadius: theme.roundness,
					backgroundColor: theme.colors.surface,
					borderColor: theme.colors.outlineVariant,
				}}
			>
				<Box padding={18} gap={6}>
					<Text
						variant="titleMedium"
						style={{ color: theme.colors.onSurface, fontWeight: "800" }}
					>
						Syllabus
					</Text>
					<Text
						variant="bodyMedium"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{config.curriculumFraming}
					</Text>
				</Box>
				<Divider />
				<Box>
					{config.modules.map((module, index) => (
						<ModuleSyllabusRow key={module.id} module={module} index={index} />
					))}
				</Box>
			</Card>
		</Box>
	);
};

export const ExpectationsStep = ({ config, isCompact }: StepProps) => {
	const theme = useAppTheme();
	const step = getStep(config, "how-you-will-begin");
	const startModule = config.modules[0];

	return (
		<Box
			flexDirection={isCompact ? "column" : "row"}
			alignItems="stretch"
			gap={isCompact ? 18 : 28}
		>
			<Box flex={1.3} gap={16}>
				<Box gap={8}>
					<Text
						variant="headlineMedium"
						style={{ color: theme.colors.onBackground, fontWeight: "800" }}
					>
						{step.title}
					</Text>
					<Text
						variant="bodyLarge"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{step.description}
					</Text>
				</Box>
				<Card
					mode="outlined"
					style={{
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surface,
						borderColor: theme.colors.outlineVariant,
					}}
				>
					<Box padding={20} gap={16}>
						{config.expectations.map((expectation) => (
							<ExpectationItem
								key={expectation.title}
								icon={expectation.icon as IconType}
								title={expectation.title}
								description={expectation.description}
							/>
						))}
					</Box>
				</Card>
			</Box>
			<Card
				mode="outlined"
				style={{
					flex: 1,
					borderRadius: theme.roundness,
					backgroundColor: theme.colors.surface,
					borderColor: theme.colors.outlineVariant,
				}}
			>
				<Box padding={20} gap={16}>
					<Text
						variant="labelLarge"
						style={{ color: theme.colors.primary, fontWeight: "800" }}
					>
						Ready to start
					</Text>
					<Text variant="titleLarge" style={{ fontWeight: "800" }}>
						{startModule
							? `Module ${startModule.moduleNumber}: ${startModule.title}`
							: "Your first module"}
					</Text>
					{startModule ? (
						<Box flexDirection="row" alignItems="center" gap={12}>
							{startModule.thumbnailSource ? (
								<Image
									source={startModule.thumbnailSource}
									style={{
										width: 76,
										height: 76,
										borderRadius: theme.roundness,
									}}
									contentFit="contain"
									alt={startModule.title}
								/>
							) : null}
							<Box flex={1} minWidth={0} gap={4}>
								<Text
									variant="bodyMedium"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{startModule.subtitle}
								</Text>
								<Text
									variant="labelMedium"
									style={{ color: theme.colors.primary, fontWeight: "800" }}
								>
									{startModule.moduleKindLabel} - {startModule.durationLabel}
								</Text>
							</Box>
						</Box>
					) : null}
					<Box
						backgroundColor={theme.colors.surfaceVariant}
						borderRadius={theme.roundness}
						padding={14}
					>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant, lineHeight: 22 }}
						>
							{config.startNote}
						</Text>
					</Box>
				</Box>
			</Card>
		</Box>
	);
};

const getStepContent = (
	stepId: OnboardingStep["id"],
	config: OnboardingConfig,
	isCompact: boolean,
) => {
	switch (stepId) {
		case "why-it-matters":
			return <WelcomeStep config={config} isCompact={isCompact} />;
		case "confirm-details":
			return <ConfirmDetailsStep config={config} isCompact={isCompact} />;
		case "what-you-will-practice":
			return <CurriculumStep config={config} isCompact={isCompact} />;
		case "how-you-will-begin":
			return <ExpectationsStep config={config} isCompact={isCompact} />;
		default:
			return null;
	}
};

export const ProethicaOnboardingFlow = ({
	config,
	activeStepIndex,
	onStepChange,
	onStartModuleOne,
}: {
	config: OnboardingConfig;
	activeStepIndex: number;
	onStepChange: (stepIndex: number) => void;
	onStartModuleOne: () => void;
}) => {
	const { width } = useWindowDimensions();
	const { isMobile } = useDevice();
	const isCompact = isMobile || width < 860;
	const currentStep = config.steps[activeStepIndex] ?? config.steps[0];
	const isLastStep = activeStepIndex === config.steps.length - 1;

	const handleBack = () => {
		onStepChange(Math.max(activeStepIndex - 1, 0));
	};

	const handleNext = () => {
		if (isLastStep) {
			onStartModuleOne();
			return;
		}
		onStepChange(Math.min(activeStepIndex + 1, config.steps.length - 1));
	};

	return (
		<OnboardingFullscreenLayout
			config={config}
			currentStep={currentStep}
			activeStepIndex={activeStepIndex}
			isCompact={isCompact}
			canGoBack={activeStepIndex > 0}
			isLastStep={isLastStep}
			onBack={handleBack}
			onNext={handleNext}
		>
			{getStepContent(currentStep.id, config, isCompact)}
		</OnboardingFullscreenLayout>
	);
};

export default ProethicaOnboardingFlow;
